import { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGrid,
  gridClasses,
  type GridColDef,
  type GridPaginationModel,
  GridToolbarContainer,
} from '@mui/x-data-grid';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100] as const;
const DATE_TIME_SUFFIX = 'T00:00:00.000';

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    rows: Array<Record<string, unknown>>;
    columns: CsvColumn[];
    fileName?: string;
  }
}

interface ExtendedHTMLDivElement extends HTMLDivElement {
  _resizeObserver?: ResizeObserver;
}

type Props = {
  tableId: string;
  columns: GridColDef[];
  rows: Array<Record<string, unknown>>;
  title?: string;
  pinnedLeft?: string[];
  showExportCsv?: boolean;
};

type CsvColumn = {
  field: string;
  label: string;
};

type CustomToolbarProps = {
  rows: Array<Record<string, unknown>>;
  columns: CsvColumn[];
  fileName?: string;
};

type PaginationProps = {
  pagination?: true;
};

type UseTablePaginationResult = {
  shouldShowPagination: boolean;
  pageSizeOptions: number[];
  effectivePaginationModel: GridPaginationModel;
  handlePaginationModelChange: (model: GridPaginationModel) => void;
  paginationProps: PaginationProps;
};

const useTablePagination = (totalRows: number): UseTablePaginationResult => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE_OPTIONS[0],
  });

  const shouldShowPagination = totalRows > PAGE_SIZE_OPTIONS[0];

  const pageSizeOptions = useMemo(() => {
    if (!shouldShowPagination) {
      return [PAGE_SIZE_OPTIONS[0]];
    }

    const options = PAGE_SIZE_OPTIONS.filter((option) => option < totalRows);
    return [...options, totalRows];
  }, [shouldShowPagination, totalRows]);

  const effectivePaginationModel = useMemo(() => {
    const fallbackPageSize = pageSizeOptions[0] ?? PAGE_SIZE_OPTIONS[0];
    const pageSize = pageSizeOptions.includes(paginationModel.pageSize)
      ? paginationModel.pageSize
      : fallbackPageSize;
    const maxPage = shouldShowPagination ? Math.max(0, Math.ceil(totalRows / pageSize) - 1) : 0;

    return {
      page: Math.min(paginationModel.page, maxPage),
      pageSize,
    };
  }, [pageSizeOptions, paginationModel, shouldShowPagination, totalRows]);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((previousModel) => {
      const pageSizeChanged = previousModel.pageSize !== model.pageSize;

      return {
        page: pageSizeChanged ? 0 : model.page,
        pageSize: model.pageSize,
      };
    });
  }, []);

  const paginationProps = useMemo<PaginationProps>(() => {
    return shouldShowPagination ? { pagination: true } : {};
  }, [shouldShowPagination]);

  return {
    shouldShowPagination,
    pageSizeOptions,
    effectivePaginationModel,
    handlePaginationModelChange,
    paginationProps,
  };
};

export const getDisplayCellValue = (cellData: unknown): string | number | boolean => {
  if (typeof cellData === 'string' || typeof cellData === 'number' || typeof cellData === 'boolean') {
    return cellData;
  }

  if (cellData !== null && typeof cellData === 'object' && 'value' in cellData) {
    const value = (cellData as { value?: unknown }).value;

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }
  }

  return '';
};

const getNumericCellValue = (cellData: unknown): number | null => {
  const value = getDisplayCellValue(cellData);

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (trimmedValue.length === 0) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  return null;
};

const stripDateTimeSuffix = (value: string): string => {
  return value.endsWith(DATE_TIME_SUFFIX) ? value.slice(0, -DATE_TIME_SUFFIX.length) : value;
};

const stripDateTimeSuffixFromCellData = (cellData: unknown): unknown => {
  if (typeof cellData === 'string') {
    return stripDateTimeSuffix(cellData);
  }

  if (cellData !== null && typeof cellData === 'object' && 'value' in cellData) {
    const objectCellData = cellData as { value?: unknown };

    if (typeof objectCellData.value === 'string') {
      return {
        ...objectCellData,
        value: stripDateTimeSuffix(objectCellData.value),
      };
    }
  }

  return cellData;
};

const CustomToolbar = ({ rows, columns, fileName = 'table-data' }: CustomToolbarProps): React.JSX.Element => {
  const handleExport = () => {
    // Convert rows to CSV format
    const csvHeaders = columns
      .map((column) => {
        const value = String(column.label);
        const escaped = value.replace(/"/g, '""');
        const needsQuotes = escaped.includes(',') || escaped.includes('\n') || escaped.includes('"');
        return needsQuotes ? '"' + escaped + '"' : escaped;
      })
      .join(',');
    const csvRows = rows
      .map((row) => {
        return columns
          .map((column) => {
            const cellData = row[column.field];
            const value = String(getDisplayCellValue(cellData));
            // Escape quotes and wrap in quotes if contains comma or newline
            const escaped = value.replace(/"/g, '""');
            return /[,\n"]/.test(escaped) ? `"${escaped}"` : escaped;
          })
          .join(',');
      })
      .join('\n');

    const csv = `${csvHeaders}\n${csvRows}`;

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <GridToolbarContainer>
      <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport}>
        Export CSV
      </Button>
    </GridToolbarContainer>
  );
};

export const Table = (props: Props) => {
  const maxTableHeight = 520;
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const gridRef = useRef<ExtendedHTMLDivElement>(null);
  const showExportCsv = props.showExportCsv ?? true;

  const pinnedLeft = props.pinnedLeft ?? [];
  const csvColumns = useMemo<CsvColumn[]>(() => {
    return props.columns.map((column) => ({
      field: column.field,
      label: column.headerName ?? column.field,
    }));
  }, [props.columns]);

  const calculateColumnWidths = useCallback(() => {
    if (gridRef.current) {
      const newWidths: Record<string, number> = {};

      props.columns.forEach((column) => {
        const headerElement = gridRef.current?.querySelector(`[data-field="${column.field}"]`);
        if (headerElement) {
          newWidths[column.field] = headerElement.getBoundingClientRect().width;
        }
      });

      setColumnWidths(newWidths);
    }
  }, [props.columns]);

  const dateOnlyColumnFields = useMemo<Set<string>>(() => {
    const fields = props.columns
      .filter((column) => {
        if (props.rows.length === 0) {
          return false;
        }

        return props.rows.every((row) => {
          const value = getDisplayCellValue(row[column.field]);
          return typeof value === 'string' && value.endsWith(DATE_TIME_SUFFIX);
        });
      })
      .map((column) => column.field);

    return new Set(fields);
  }, [props.columns, props.rows]);

  const rows = useMemo<Array<Record<string, unknown>>>(() => {
    return props.rows.map((row, index) => {
      const normalizedRow: Record<string, unknown> = {
        ...row,
        _gridId: `${props.tableId}-${index}`,
      };

      dateOnlyColumnFields.forEach((field) => {
        normalizedRow[field] = stripDateTimeSuffixFromCellData(row[field]);
      });

      return normalizedRow;
    });
  }, [dateOnlyColumnFields, props.rows, props.tableId]);

  const numericColumnFields = useMemo<Set<string>>(() => {
    const fields = props.columns
      .filter((column) => {
        if (props.rows.length === 0) {
          return false;
        }

        return props.rows.every((row) => getNumericCellValue(row[column.field]) !== null);
      })
      .map((column) => column.field);

    return new Set(fields);
  }, [props.columns, props.rows]);

  const rightAlignedColumnFields = useMemo<Set<string>>(() => {
    return new Set([...numericColumnFields, ...dateOnlyColumnFields]);
  }, [dateOnlyColumnFields, numericColumnFields]);

  const columns = useMemo<GridColDef[]>(() => {
    return props.columns.map((column) => {
      if (!rightAlignedColumnFields.has(column.field)) {
        return column;
      }

      return {
        ...column,
        align: 'right',
      };
    });
  }, [props.columns, rightAlignedColumnFields]);

  const {
    shouldShowPagination,
    pageSizeOptions,
    effectivePaginationModel,
    handlePaginationModelChange,
    paginationProps,
  } = useTablePagination(rows.length);

  useLayoutEffect(() => {
    calculateColumnWidths();
  }, [props.columns, rows, calculateColumnWidths]);

  useEffect(() => {
    return () => {
      if (gridRef.current) {
        const observer = gridRef.current._resizeObserver;

        if (observer) {
          observer.disconnect();
        }
      }
    };
  }, []);

  const gridCallbackRef = useCallback(
    (node: ExtendedHTMLDivElement | null) => {
      if (gridRef.current && gridRef.current !== node) {
        const observer = gridRef.current._resizeObserver;

        if (observer) {
          observer.disconnect();
          delete gridRef.current._resizeObserver;
        }
      }

      gridRef.current = node;

      if (node) {
        const resizeObserver = new ResizeObserver(() => {
          calculateColumnWidths();
        });

        resizeObserver.observe(node);
        node._resizeObserver = resizeObserver;

        calculateColumnWidths();
      }
    },
    [calculateColumnWidths]
  );

  return (
    <div
      ref={gridCallbackRef}
      style={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: `${maxTableHeight}px`,
        marginTop: '16px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <DataGridStriped
        {...paginationProps}
        pinnedColumns={pinnedLeft}
        columnWidths={columnWidths}
        disableRowSelectionOnClick={true}
        rowSelection={false}
        rows={rows}
        columns={columns}
        getRowId={(row) => row._gridId}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        autosizeOptions={{
          includeHeaders: true,
          includeOutliers: true,
        }}
        autosizeOnMount={true}
        getRowHeight={() => 'auto'}
        hideFooterPagination={!shouldShowPagination}
        paginationModel={effectivePaginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={pageSizeOptions}
        slots={showExportCsv ? { toolbar: CustomToolbar } : undefined}
        slotProps={
          showExportCsv
            ? {
                toolbar: {
                  rows,
                  columns: csvColumns,
                  fileName: props.title || 'table-data',
                },
              }
            : undefined
        }
        sx={{
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
          },
          '& .MuiDataGrid-columnHeader': {
            fontWeight: 'bold !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold !important',
          },
        }}
      />
    </div>
  );
};

Table.displayName = 'Chat.Attachments.Table';

const DataGridStriped = styled(DataGrid)<{ pinnedColumns: string[]; columnWidths: Record<string, number> }>(({
  theme: _,
  pinnedColumns,
  columnWidths,
}) => {
  const pinnedStyles: Record<string, unknown> = {};

  let cumulativeLeft = 0;
  pinnedColumns.forEach((field, index) => {
    pinnedStyles[
      `& .MuiDataGrid-cell[data-field="${field}"], & .MuiDataGrid-columnHeader[data-field="${field}"]`
    ] = {
      position: 'sticky',
      left: cumulativeLeft,
      backgroundColor: 'inherit',
      zIndex: pinnedColumns.length - index + 1,
      borderRight: index === pinnedColumns.length - 1 ? '2px solid #e0e0e0' : '1px solid #e0e0e0',
    };

    pinnedStyles[`& .MuiDataGrid-columnHeader[data-field="${field}"]`] = {
      position: 'sticky',
      left: cumulativeLeft,
      backgroundColor: '#ffffff !important',
      zIndex: pinnedColumns.length - index + 2,
      borderRight: index === pinnedColumns.length - 1 ? '2px solid #e0e0e0' : '1px solid #e0e0e0',
      borderLeft: 'none !important',
      boxShadow: 'none !important',
      '&::before': {
        display: 'none !important',
      },
      '&::after': {
        display: 'none !important',
      },
      '& .MuiDataGrid-columnSeparator': {
        display: 'none !important',
      },
      '& .MuiDataGrid-iconSeparator': {
        display: 'none !important',
      },
    };

    pinnedStyles[`& .MuiDataGrid-columnHeader[data-field="${field}"] .MuiDataGrid-columnSeparator`] = {
      display: 'none !important',
    };

    cumulativeLeft += columnWidths[field] || 100;
  });

  return {
    width: '100%',
    '& .MuiDataGrid-root': {
      overflow: 'auto',
    },
    '& .MuiDataGrid-virtualScroller': {
      overflow: 'auto',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none !important',
    },
    '& .MuiDataGrid-iconSeparator': {
      display: 'none !important',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderRight: 'none !important',
    },
    ...pinnedStyles,
    [`& .${gridClasses.row}.odd`]: {
      backgroundColor: '#f6f8fa',
      '&:hover': {
        backgroundColor: '#f6f8fa',
      },
      '&.Mui-selected': {
        backgroundColor: '#f6f8fa',
        '&:hover': {
          backgroundColor: '#f6f8fa',
        },
      },
    },
    [`& .${gridClasses.row}.even`]: {
      backgroundColor: '#ffffff',
      '&:hover': {
        backgroundColor: '#ffffff',
      },
      '&.Mui-selected': {
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#ffffff',
        },
      },
    },
    [`& .${gridClasses.cell}`]: {
      outline: 'transparent',
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      lineHeight: '1.4',
      padding: '8px',
    },
    [`& .${gridClasses.cell}:focus-within`]: {
      outline: 'none',
    },
    [`& .${gridClasses.columnHeader}`]: {
      fontWeight: 'bold',
    },
  };
});
